import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Minus, Plus, ShieldCheck, Truck, RefreshCcw, X, ZoomIn } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/img";

function ImageGallery({ images, name }) {
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const wrapRef = useRef(null);

  useEffect(() => { setIdx(0); }, [images]);

  const onMove = (e) => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const main = imgSrc(images[idx]);

  return (
    <div data-testid="product-gallery">
      <div
        ref={wrapRef}
        onMouseMove={onMove}
        onMouseEnter={() => setZoom(true)}
        onMouseLeave={() => setZoom(false)}
        onClick={() => setZoom(z => !z)}
        className="relative aspect-[3/4] bg-[#F3EFEA] overflow-hidden cursor-zoom-in"
        data-testid="gallery-main"
      >
        <img
          src={main}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-300"
          style={{
            transform: zoom ? "scale(2)" : "scale(1)",
            transformOrigin: `${pos.x}% ${pos.y}%`,
          }}
        />
        {!zoom && (
          <div className="absolute bottom-4 right-4 bg-[#FAFAF7]/90 backdrop-blur px-3 py-2 text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 pointer-events-none">
            <ZoomIn size={14} strokeWidth={1.5} /> Hover to zoom
          </div>
        )}
      </div>

      {images.length > 1 && (
        <div className="mt-4 grid grid-cols-4 gap-3" data-testid="gallery-thumbs">
          {images.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              data-testid={`gallery-thumb-${i}`}
              className={`relative aspect-[3/4] bg-[#F3EFEA] overflow-hidden border-2 transition-colors ${i === idx ? "border-[#6B1A24]" : "border-transparent hover:border-[#E5DFD3]"}`}
            >
              <img src={src} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { API } = useAuth();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("M");
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(r => {
      setP(r.data);
      setSize(r.data.sizes?.[0] || "M");
      setColor(r.data.colors?.[0] || null);
    }).catch(() => toast.error("Product not found"));
  }, [id, API]);

  if (!p) return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 text-sm text-[#5C5454]" data-testid="product-loading">Loading piece…</div>;

  const soldOut = p.stock === 0;
  const lowStock = !soldOut && p.stock <= 5;
  const maxQty = Math.max(1, p.stock || 1);

  const handleAdd = () => {
    if (soldOut) return;
    add(p, size, color, qty);
    toast.success(`${p.name} added to cart`, { description: `Size ${size}${color ? " · " + color : ""}` });
  };

  const buyNow = () => { if (soldOut) return; handleAdd(); nav("/cart"); };

  return (
    <div data-testid="product-detail" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <ImageGallery images={p.images?.length ? p.images : [""]} name={p.name} />

        <div className="lg:pl-8">
          <div className="label-eyebrow">{p.category}</div>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight" data-testid="product-name">{p.name}</h1>
          <div className="mt-5 flex items-baseline gap-4">
            <div className="font-display text-3xl text-[#6B1A24]" data-testid="product-price">${p.price.toFixed(2)}</div>
            {soldOut && <span data-testid="detail-sold-out" className="text-xs uppercase tracking-[0.25em] bg-[#2D2626] text-[#FAFAF7] px-3 py-1">Sold Out</span>}
            {lowStock && <span data-testid="detail-low-stock" className="text-xs uppercase tracking-[0.25em] bg-[#D4AF37] text-[#2D2626] px-3 py-1">Only {p.stock} left</span>}
          </div>

          <p className="mt-8 text-sm leading-relaxed text-[#5C5454]" data-testid="product-description">{p.description}</p>

          {/* Size */}
          <div className="mt-10">
            <div className="label-eyebrow">Size</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(p.sizes || []).map(s => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  data-testid={`size-${s}`}
                  className={`min-w-[3rem] px-4 py-3 text-sm border transition-colors ${size === s ? "bg-[#2D2626] text-[#FAFAF7] border-[#2D2626]" : "border-[#E5DFD3] hover:border-[#2D2626]"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Colors */}
          {p.colors && p.colors.length > 0 && (
            <div className="mt-8">
              <div className="label-eyebrow">Color · <span className="text-[#5C5454] normal-case tracking-normal">{color}</span></div>
              <div className="mt-3 flex flex-wrap gap-2">
                {p.colors.map(c => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    data-testid={`color-${c}`}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${color === c ? "bg-[#6B1A24] text-[#FAFAF7] border-[#6B1A24]" : "border-[#E5DFD3] hover:border-[#6B1A24]"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mt-8 flex items-center gap-6">
            <div className="label-eyebrow">Quantity</div>
            <div className="flex items-center border border-[#E5DFD3]">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} data-testid="qty-dec" disabled={soldOut} className="p-3 hover:bg-[#F3EFEA] disabled:opacity-40"><Minus size={14} strokeWidth={1.5} /></button>
              <span className="px-5 text-sm tabular-nums" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(q => Math.min(maxQty, q + 1))} data-testid="qty-inc" disabled={soldOut || qty >= maxQty} className="p-3 hover:bg-[#F3EFEA] disabled:opacity-40"><Plus size={14} strokeWidth={1.5} /></button>
            </div>
            {!soldOut && (
              <span className="text-xs text-[#5C5454]" data-testid="stock-info">{p.stock} in stock</span>
            )}
          </div>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAdd}
              disabled={soldOut}
              data-testid="add-to-cart-button"
              className="flex-1 bg-[#6B1A24] text-[#FAFAF7] px-8 py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#8A2532] transition-colors duration-500 disabled:bg-[#5C5454] disabled:cursor-not-allowed"
            >
              {soldOut ? "Sold Out" : "Add to Cart"}
            </button>
            <button
              onClick={buyNow}
              disabled={soldOut}
              data-testid="buy-now-button"
              className="flex-1 border border-[#2D2626] px-8 py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#2D2626] hover:text-[#FAFAF7] transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Buy It Now
            </button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 text-xs text-[#5C5454]">
            <div className="flex items-center gap-2"><Truck size={16} strokeWidth={1.5} /> Free over $150</div>
            <div className="flex items-center gap-2"><RefreshCcw size={16} strokeWidth={1.5} /> 14-day returns</div>
            <div className="flex items-center gap-2"><ShieldCheck size={16} strokeWidth={1.5} /> Secure pay</div>
          </div>
        </div>
      </div>
    </div>
  );
}
